import { buildFormula } from './operation'

// sparkUpliftCpmSum / sparkMeasuredBaselineCpmSum are precomputed server-side, per (bidder, time
// bucket), floored at 0 there — see query-spark-for-pub.ts. That makes every formula below linear in
// its inputs, which is what lets buildFormula's sum() (which sums the input arrays across buckets
// then applies raw once) agree with the sum of the per-bucket bars, and with the by-bidder
// breakdown's own sum: there is no ratio-with-a-floor left on the frontend to break that.
const toRevenue = (cpmSum: number) => cpmSum / 1000
const rpm = (cpmSum: number, adCalls: number) => (adCalls ? cpmSum / adCalls : 0)

// baseline_bt (and so sparkMeasuredBaselineCpmSum) is legitimately 0 when a control sample existed
// but won nothing this bucket, not only when there was no control sample at all — a live division,
// so both must be guarded, or a "won nothing everywhere" bidder/period renders Infinity instead of N/A.
const revenueUpliftPctRaw = (sparkUpliftCpmSum: number, sparkMeasuredBaselineCpmSum: number, hasControlSample: boolean) =>
  !hasControlSample || sparkMeasuredBaselineCpmSum === 0 ? NaN : (sparkUpliftCpmSum / sparkMeasuredBaselineCpmSum) * 100

export const sparkForPubTotalRevenue = buildFormula({
  name: 'Revenue',
  keys: ['controlImpressionCpmSum', 'sparkImpressionCpmSum'],
  raw: ({ controlImpressionCpmSum, sparkImpressionCpmSum }) => toRevenue(controlImpressionCpmSum + sparkImpressionCpmSum),
  type: 'monetary',
  isStackable: true,
})

export const sparkForPubBaselineRevenue = buildFormula({
  name: 'Baseline',
  keys: ['controlImpressionCpmSum', 'sparkImpressionCpmSum', 'sparkUpliftCpmSum'],
  raw: ({ controlImpressionCpmSum, sparkImpressionCpmSum, sparkUpliftCpmSum }) => toRevenue(controlImpressionCpmSum + sparkImpressionCpmSum - sparkUpliftCpmSum),
  type: 'monetary',
  isStackable: true,
})

export const sparkForPubUpliftRevenue = buildFormula({
  name: 'Spark Uplift',
  keys: ['sparkUpliftCpmSum'],
  raw: ({ sparkUpliftCpmSum }) => toRevenue(sparkUpliftCpmSum),
  type: 'monetary',
  isStackable: true,
})

// Absolute incremental revenue from Spark = total − baseline (baseline = all ad calls projected at control RPM).
export const sparkForPubRevenueUplift = buildFormula({
  name: 'Rev. Uplift',
  keys: ['sparkUpliftCpmSum'],
  raw: sparkForPubUpliftRevenue.raw,
  type: 'monetary',
  isStackable: false,
})

// % counterpart of sparkForPubRevenueUplift — revenue uplift relative to the baseline (all ad calls at
// control RPM). Used only by-bidder today (see SparkForPubBreakdown.tsx), where controlAdCallCount
// already lives in the bid universe those atoms are built from, so it's a sufficient guard on its own.
export const sparkForPubRevenueUpliftPct = buildFormula({
  name: 'Spark Uplift',
  keys: ['sparkUpliftCpmSum', 'sparkMeasuredBaselineCpmSum', 'controlAdCallCount'],
  raw: ({ sparkUpliftCpmSum, sparkMeasuredBaselineCpmSum, controlAdCallCount }) => revenueUpliftPctRaw(sparkUpliftCpmSum, sparkMeasuredBaselineCpmSum, controlAdCallCount !== 0),
  type: 'signedUplift',
  isStackable: false,
})

// % counterpart of sparkForPubRpmUpliftAmount (below) — RPM uplift relative to the control-group RPM.
// Same math as sparkForPubRevenueUpliftPct (uplift/baseline — a ratio of ratios wouldn't aggregate,
// which is why both share one definition), but this one is also summed by time (the timeline banner),
// where controlAdCallCount is the unrelated auction-scoped field — sparkControlAdCallCount is the
// bid-universe count that actually backs sparkUpliftCpmSum/sparkMeasuredBaselineCpmSum there, and is
// simply absent (not present-as-zero) on by-bidder data, hence falling back to controlAdCallCount.
export const sparkForPubRpmUplift = buildFormula({
  name: 'RPM Uplift %',
  keys: ['sparkUpliftCpmSum', 'sparkMeasuredBaselineCpmSum', 'controlAdCallCount', 'sparkControlAdCallCount'],
  optionalKeys: ['sparkControlAdCallCount'],
  raw: ({ sparkUpliftCpmSum, sparkMeasuredBaselineCpmSum, controlAdCallCount, sparkControlAdCallCount }) =>
    revenueUpliftPctRaw(sparkUpliftCpmSum, sparkMeasuredBaselineCpmSum, (sparkControlAdCallCount ?? controlAdCallCount) !== 0),
  type: 'signedUplift',
  isStackable: false,
})

// RPM split into stacked baseline + uplift (mirrors the revenue trio), normalized per ad call:
// baseline = control RPM projected over all ad calls, uplift = totalRPM - baseline, so baseline + uplift = totalRPM.
// serverSideAdCallCount (not controlAdCallCount + sparkAdCallCount) is the denominator throughout,
// because by time those are now different universes (bid requests vs auctions) and
// serverSideAdCallCount is the Ad Calls figure actually displayed — this keeps
// RPM Uplift = Revenue Uplift x 1000 / Ad Calls true by construction at every level.
export const sparkForPubTotalRpm = buildFormula({
  name: 'RPM',
  keys: ['controlImpressionCpmSum', 'sparkImpressionCpmSum', 'serverSideAdCallCount'],
  raw: ({ controlImpressionCpmSum, sparkImpressionCpmSum, serverSideAdCallCount }) => rpm(controlImpressionCpmSum + sparkImpressionCpmSum, serverSideAdCallCount),
  type: 'preciseMonetary',
  isStackable: true,
})

export const sparkForPubBaselineRpm = buildFormula({
  name: 'Baseline',
  keys: ['controlImpressionCpmSum', 'sparkImpressionCpmSum', 'sparkUpliftCpmSum', 'serverSideAdCallCount'],
  raw: ({ controlImpressionCpmSum, sparkImpressionCpmSum, sparkUpliftCpmSum, serverSideAdCallCount }) =>
    rpm(controlImpressionCpmSum + sparkImpressionCpmSum - sparkUpliftCpmSum, serverSideAdCallCount),
  type: 'preciseMonetary',
  isStackable: true,
})

export const sparkForPubUpliftRpm = buildFormula({
  name: 'Spark Uplift',
  keys: ['sparkUpliftCpmSum', 'serverSideAdCallCount'],
  raw: ({ sparkUpliftCpmSum, serverSideAdCallCount }) => rpm(sparkUpliftCpmSum, serverSideAdCallCount),
  type: 'preciseMonetary',
  isStackable: true,
})

// Same computation as sparkForPubUpliftRpm, surfaced under the breakdown's own naming ("RPM Uplift" raw amount,
// vs. sparkForPubRpmUplift for its % counterpart) — sparkForPubUpliftRpm's name is scoped to the timeline's stacked chart.
// keys re-declared as its own literal for the same reason as sparkForPubRpmUplift above.
export const sparkForPubRpmUpliftAmount = buildFormula({
  name: 'RPM Uplift',
  keys: ['sparkUpliftCpmSum', 'serverSideAdCallCount'],
  raw: sparkForPubUpliftRpm.raw,
  type: 'preciseMonetary',
  isStackable: false,
})

// Ad calls: the auctions (by time) or bid requests (by bidder) that would have happened without
// traffic shaping — logged count plus what shaping removed.
export const sparkForPubAdCallCount = buildFormula({
  name: 'Ad Calls',
  keys: ['serverSideAdCallCount'],
  raw: ({ serverSideAdCallCount }) => serverSideAdCallCount,
  type: 'shortNumber',
  isStackable: true,
})
